<template>
  <div v-if="show" id="lab-report-modal-wrapper" class="fixed inset-0 z-[999] bg-white overflow-y-auto">

    <!-- Controls — hidden when printing -->
    <div class="print:hidden sticky top-0 z-10 flex justify-between items-center px-6 py-3 bg-gray-100 border-b shadow-sm">
      <h2 class="font-semibold text-gray-700">
        Lab Report Preview
        <span class="font-normal text-gray-500 ml-2">— {{ labTest?.test_type?.name }}</span>
      </h2>
      <div class="flex gap-3">
        <button
          @click="$emit('close')"
          class="px-4 py-1.5 text-sm border border-gray-300 rounded text-gray-600 hover:bg-gray-200"
        >
          Close
        </button>
        <button
          @click="printReport"
          :disabled="loading"
          class="px-4 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
        >
          Print Report
        </button>
      </div>
    </div>

    <!-- Loader -->
    <div v-if="loading" class="flex items-center justify-center h-64">
      <div class="animate-spin rounded-full h-10 w-10 border-t-4 border-b-4 border-blue-600"></div>
    </div>

    <!-- Report body -->
    <div v-else id="lab-report-content" class="max-w-4xl mx-auto px-10 py-8 text-sm text-gray-900">

      <!-- Facility Header -->
      <div class="text-center border-b-2 border-gray-800 pb-4 mb-5">
        <img v-if="facility?.logo_url" :src="facility.logo_url" alt="Logo" class="h-16 object-contain mx-auto mb-1" />
        <p class="text-xl font-bold">{{ facility?.FacilityName || 'Health Facility' }}</p>
        <p class="text-xs text-gray-500 mt-1">
          {{ [facility?.NearestTown, facility?.County].filter(Boolean).join(', ') }}
        </p>
        <p class="text-sm font-bold tracking-widest mt-2 uppercase">
          Laboratory Report — {{ disciplineLabel }}
        </p>
      </div>

      <!-- Patient Info Grid -->
      <div class="grid grid-cols-2 gap-x-8 gap-y-1 bg-gray-50 border border-gray-200 rounded px-4 py-3 mb-5 text-xs">
        <div class="flex gap-2">
          <span class="font-semibold w-32 shrink-0">Patient Name:</span>
          <span>{{ patient?.FullName }}</span>
        </div>
        <div class="flex gap-2">
          <span class="font-semibold w-32 shrink-0">Visit No.:</span>
          <span>{{ visit?.VisitCode }}</span>
        </div>
        <div class="flex gap-2">
          <span class="font-semibold w-32 shrink-0">Date of Birth:</span>
          <span>{{ formatDate(patient?.Dob) }}</span>
        </div>
        <div class="flex gap-2">
          <span class="font-semibold w-32 shrink-0">Date Collected:</span>
          <span>{{ formatDateTime(labTest?.collected_at) }}</span>
        </div>
        <div class="flex gap-2">
          <span class="font-semibold w-32 shrink-0">Age:</span>
          <span>{{ patientAge != null ? patientAge + ' yrs' : '—' }}</span>
        </div>
        <div class="flex gap-2">
          <span class="font-semibold w-32 shrink-0">Gender:</span>
          <span>{{ patient?.Gender }}</span>
        </div>
        <div class="flex gap-2">
          <span class="font-semibold w-32 shrink-0">Date Reported:</span>
          <span>{{ formatDateTime(new Date().toISOString()) }}</span>
        </div>
        <div class="flex gap-2">
          <span class="font-semibold w-32 shrink-0">Requesting Dr.:</span>
          <span>{{ labTest?.requested_by?.FullName || labTest?.requested_by?.Username || '—' }}</span>
        </div>
        <div class="flex gap-2">
          <span class="font-semibold w-32 shrink-0">Sample Type:</span>
          <span>{{ labTest?.sample_type_override || labTest?.test_type?.sample?.name || '—' }}</span>
        </div>
      </div>

      <!-- No structured results yet -->
      <div v-if="!analyteResults.length" class="text-center py-10 text-gray-400">
        <p class="font-medium text-gray-600">Structured analyte results not yet captured.</p>
        <p class="text-xs mt-1">This test may have been entered as free-text before the structured result system was in place.</p>
      </div>

      <template v-else>

        <!-- ══ HAEMATOLOGY ══ -->
        <template v-if="discipline === 'haematology'">
          <p class="text-xs font-bold uppercase border-b border-gray-700 pb-1 mb-2 tracking-wide">Full Blood Count</p>
          <table class="w-full text-xs border-collapse mb-4">
            <thead>
              <tr class="bg-gray-800 text-white text-left">
                <th class="px-3 py-2 w-5/12">Test</th>
                <th class="px-3 py-2 w-2/12 text-right">Result</th>
                <th class="px-3 py-2 w-2/12">Units</th>
                <th class="px-3 py-2 w-2/12">Reference Range</th>
                <th class="px-3 py-2 w-1/12 text-center">Flag</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(r, i) in analyteResults"
                :key="r.id"
                :class="r.is_abnormal ? 'bg-amber-50' : i % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
              >
                <td class="px-3 py-1.5 border-b border-gray-100">{{ r.lab_analyte.name }}</td>
                <td class="px-3 py-1.5 border-b border-gray-100 text-right font-semibold">{{ r.value }}</td>
                <td class="px-3 py-1.5 border-b border-gray-100 text-gray-600">{{ r.unit }}</td>
                <td class="px-3 py-1.5 border-b border-gray-100 text-gray-600">
                  <span v-if="r.reference_range_low != null || r.reference_range_high != null">
                    {{ r.reference_range_low }} &ndash; {{ r.reference_range_high }}
                  </span>
                </td>
                <td class="px-3 py-1.5 border-b border-gray-100 text-center">
                  <span v-if="r.flag === 'H'" class="font-bold text-red-600">H</span>
                  <span v-else-if="r.flag === 'L'" class="font-bold text-blue-600">L</span>
                </td>
              </tr>
            </tbody>
          </table>
        </template>

        <!-- ══ BIOCHEMISTRY ══ -->
        <template v-else-if="discipline === 'biochemistry'">
          <div v-for="group in biochemistryGroups" :key="group.section" class="mb-5">
            <p class="text-xs font-bold uppercase border-b border-gray-700 pb-1 mb-2 tracking-wide">
              {{ group.label }}
            </p>
            <table class="w-full text-xs border-collapse">
              <thead>
                <tr class="bg-gray-800 text-white text-left">
                  <th class="px-3 py-2 w-5/12">Analyte</th>
                  <th class="px-3 py-2 w-2/12 text-right">Result</th>
                  <th class="px-3 py-2 w-2/12">Units</th>
                  <th class="px-3 py-2 w-2/12">Reference Range</th>
                  <th class="px-3 py-2 w-1/12 text-center">Flag</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(r, i) in group.rows"
                  :key="r.id"
                  :class="r.is_abnormal ? 'bg-amber-50' : i % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                >
                  <td class="px-3 py-1.5 border-b border-gray-100">{{ r.lab_analyte.name }}</td>
                  <td class="px-3 py-1.5 border-b border-gray-100 text-right font-semibold">{{ r.value }}</td>
                  <td class="px-3 py-1.5 border-b border-gray-100 text-gray-600">{{ r.unit }}</td>
                  <td class="px-3 py-1.5 border-b border-gray-100 text-gray-600">
                    <span v-if="r.reference_range_low != null || r.reference_range_high != null">
                      {{ r.reference_range_low }} &ndash; {{ r.reference_range_high }}
                    </span>
                  </td>
                  <td class="px-3 py-1.5 border-b border-gray-100 text-center">
                    <span v-if="r.flag === 'H'" class="font-bold text-red-600">H</span>
                    <span v-else-if="r.flag === 'L'" class="font-bold text-blue-600">L</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>

        <!-- ══ IMMUNOASSAY ══ -->
        <template v-else-if="discipline === 'immunoassay'">
          <!-- TFTs: table with reference ranges -->
          <template v-if="immunoassayTfts.length">
            <p class="text-xs font-bold uppercase border-b border-gray-700 pb-1 mb-2 tracking-wide">
              Thyroid Function Tests
            </p>
            <table class="w-full text-xs border-collapse mb-5">
              <thead>
                <tr class="bg-gray-800 text-white text-left">
                  <th class="px-3 py-2">Analyte</th>
                  <th class="px-3 py-2 text-right">Result</th>
                  <th class="px-3 py-2">Units</th>
                  <th class="px-3 py-2">Reference Range</th>
                  <th class="px-3 py-2 text-center">Flag</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(r, i) in immunoassayTfts"
                  :key="r.id"
                  :class="r.is_abnormal ? 'bg-amber-50' : i % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                >
                  <td class="px-3 py-1.5 border-b border-gray-100">{{ r.lab_analyte.name }}</td>
                  <td class="px-3 py-1.5 border-b border-gray-100 text-right font-semibold">{{ r.value }}</td>
                  <td class="px-3 py-1.5 border-b border-gray-100 text-gray-600">{{ r.unit }}</td>
                  <td class="px-3 py-1.5 border-b border-gray-100 text-gray-600">
                    <span v-if="r.reference_range_low != null || r.reference_range_high != null">
                      {{ r.reference_range_low }} &ndash; {{ r.reference_range_high }}
                    </span>
                  </td>
                  <td class="px-3 py-1.5 border-b border-gray-100 text-center">
                    <span v-if="r.flag === 'H'" class="font-bold text-red-600">H</span>
                    <span v-else-if="r.flag === 'L'" class="font-bold text-blue-600">L</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </template>

          <!-- All other immunoassays: result + interpretation note -->
          <template v-if="immunoassayOthers.length">
            <p class="text-xs font-bold uppercase border-b border-gray-700 pb-1 mb-3 tracking-wide">
              Other Immunoassays
            </p>
            <div v-for="r in immunoassayOthers" :key="r.id" class="mb-4">
              <p class="text-xs">
                <span class="font-semibold">{{ r.lab_analyte.name }}:</span>
                <span class="ml-2 font-bold">{{ r.value }}</span>
                <span v-if="r.unit" class="ml-1 text-gray-500">{{ r.unit }}</span>
              </p>
              <div
                v-if="r.lab_analyte.interpretation_note"
                class="mt-1 text-xs text-gray-700 bg-blue-50 border-l-4 border-blue-400 px-3 py-2 rounded-r"
              >
                {{ r.lab_analyte.interpretation_note }}
              </div>
            </div>
          </template>
        </template>

        <!-- ══ URINALYSIS ══ (non-table, label-value format) -->
        <template v-else-if="discipline === 'urinalysis'">
          <div v-for="group in urinalysisGroups" :key="group.section" class="mb-5">
            <p class="text-xs font-bold uppercase border-b border-gray-700 pb-1 mb-2 tracking-wide">
              {{ group.label }}
            </p>
            <div
              v-for="r in group.rows"
              :key="r.id"
              class="flex items-center py-1 border-b border-gray-100 text-xs"
            >
              <span class="font-semibold w-44 shrink-0 text-gray-700">{{ r.lab_analyte.name }}</span>
              <span class="flex-1 font-semibold">
                {{ r.value }}<span v-if="r.unit" class="font-normal text-gray-500 ml-1">{{ r.unit }}</span>
              </span>
              <span v-if="r.flag === 'H'" class="font-bold text-red-600 w-5 text-right">H</span>
              <span v-else-if="r.flag === 'L'" class="font-bold text-blue-600 w-5 text-right">L</span>
              <span v-else class="w-5"></span>
            </div>
          </div>
        </template>

        <!-- ══ STOOL PARASITOLOGY ══ (stepwise outline) -->
        <template v-else-if="discipline === 'stool_parasitology'">
          <div v-for="group in stoolGroups" :key="group.section" class="mb-5">
            <p class="text-xs font-bold uppercase border-b border-gray-700 pb-1 mb-2 tracking-wide">
              {{ group.label }}
            </p>
            <div
              v-for="r in group.rows"
              :key="r.id"
              class="flex items-center py-1 border-b border-gray-100 text-xs"
            >
              <span class="font-semibold w-44 shrink-0 text-gray-700">{{ r.lab_analyte.name }}</span>
              <span class="flex-1 font-semibold">{{ r.value }}</span>
            </div>
          </div>
          <template v-if="comment">
            <p class="text-xs font-bold uppercase border-b border-gray-700 pb-1 mb-2 tracking-wide">
              Impression / Conclusion
            </p>
            <div class="text-xs bg-green-50 border-l-4 border-green-500 px-3 py-2 rounded-r text-gray-800">
              {{ comment }}
            </div>
          </template>
        </template>

        <!-- ══ FALLBACK ══ -->
        <template v-else>
          <p class="text-xs font-bold uppercase border-b border-gray-700 pb-1 mb-2 tracking-wide">Results</p>
          <div v-for="r in analyteResults" :key="r.id" class="flex py-1 border-b border-gray-100 text-xs">
            <span class="font-semibold w-44 shrink-0">{{ r.lab_analyte.name }}</span>
            <span class="flex-1">{{ r.value }} {{ r.unit }}</span>
            <span v-if="r.flag === 'H'" class="font-bold text-red-600">H</span>
            <span v-else-if="r.flag === 'L'" class="font-bold text-blue-600">L</span>
          </div>
        </template>

      </template>

      <!-- Comments (all disciplines except stool, which uses comments as Impression) -->
      <div
        v-if="comment && discipline !== 'stool_parasitology'"
        class="mt-5 border border-gray-200 rounded px-3 py-2 bg-gray-50 text-xs"
      >
        <span class="font-semibold">Comments:</span> {{ comment }}
      </div>

      <!-- Signature block -->
      <div class="flex gap-10 mt-10">
        <div class="flex-1 border-t border-gray-700 pt-2 text-xs text-gray-600">
          <p class="font-semibold text-gray-900">
            {{ labTest?.collecter?.FullName || labTest?.collecter?.Username || '________________________' }}
          </p>
          <p>Tested by — Lab Technician</p>
        </div>
        <div class="flex-1 border-t border-gray-700 pt-2 text-xs text-gray-600">
          <p class="font-semibold text-gray-900">
            {{ labTest?.reviewer?.FullName || labTest?.reviewer?.Username || '________________________' }}
          </p>
          <p>Reviewed &amp; Authorised by</p>
        </div>
      </div>

      <p class="text-center text-gray-400 text-xs mt-6 border-t border-gray-100 pt-3">
        This report is confidential and intended solely for the named patient and requesting clinician.
        Results are valid at the time of sample collection.
      </p>

      <div class="print-footer">
        {{ facility?.FacilityName || '' }} · Confidential
      </div>
    </div>
  </div>
</template>


<script>
import apiClient from "@/services/api";

export default {
  name: "LabReportModal",
  props: {
    show:    { type: Boolean, required: true },
    labTest: { type: Object, required: true },
    patient: { type: Object, required: true },
    visit:   { type: Object, required: true },
  },
  emits: ["close"],
  data() {
    return {
      facility: null,
      loading: false,
      SECTION_LABELS: {
        uecs:               "Urea, Electrolytes & Creatinine",
        lfts:               "Liver Function Tests",
        lipids:             "Lipid Profile",
        glucose:            "Blood Glucose",
        physical:           "Physical Examination",
        chemical:           "Chemical Analysis (Dipstick)",
        microscopy:         "Microscopy",
        general_appearance: "Macroscopic Examination",
      },
    };
  },
  computed: {
    discipline() {
      return this.labTest?.test_type?.discipline;
    },
    disciplineLabel() {
      const map = {
        haematology:        "Haematology",
        biochemistry:       "Biochemistry",
        immunoassay:        "Immunoassay",
        urinalysis:         "Urinalysis",
        stool_parasitology: "Stool & Parasitology",
      };
      return map[this.discipline] || (this.discipline || "").replace(/_/g, " ");
    },
    analyteResults() {
      return [...(this.labTest?.analyte_results || [])]
        .sort((a, b) => a.lab_analyte.sort_order - b.lab_analyte.sort_order);
    },
    comment() {
      return this.labTest?.lab_results?.[0]?.comments || "";
    },
    biochemistryGroups() {
      const ORDER = ["uecs", "lfts", "lipids", "glucose"];
      const grouped = {};
      this.analyteResults.forEach(r => {
        const s = r.lab_analyte.report_section;
        if (!grouped[s]) grouped[s] = [];
        grouped[s].push(r);
      });
      return ORDER.filter(s => grouped[s]).map(s => ({
        section: s, label: this.SECTION_LABELS[s] || s, rows: grouped[s],
      }));
    },
    immunoassayTfts() {
      return this.analyteResults.filter(r => r.lab_analyte.report_section === "tfts");
    },
    immunoassayOthers() {
      return this.analyteResults.filter(r => r.lab_analyte.report_section !== "tfts");
    },
    urinalysisGroups() {
      const ORDER = ["physical", "chemical", "microscopy"];
      const grouped = {};
      this.analyteResults.forEach(r => {
        const s = r.lab_analyte.report_section;
        if (!grouped[s]) grouped[s] = [];
        grouped[s].push(r);
      });
      return ORDER.filter(s => grouped[s]).map(s => ({
        section: s, label: this.SECTION_LABELS[s] || s, rows: grouped[s],
      }));
    },
    stoolGroups() {
      const ORDER = ["general_appearance", "microscopy"];
      const grouped = {};
      this.analyteResults.forEach(r => {
        const s = r.lab_analyte.report_section;
        if (!grouped[s]) grouped[s] = [];
        grouped[s].push(r);
      });
      return ORDER.filter(s => grouped[s]).map(s => ({
        section: s, label: this.SECTION_LABELS[s] || s, rows: grouped[s],
      }));
    },
    patientAge() {
      if (!this.patient?.Dob) return null;

      const dob = new Date(this.patient.Dob)
      const today = new Date()
      let years = today.getFullYear() - dob.getFullYear()
      const m = today.getMonth() - dob.getMonth()
      
      if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) years--
      return years
    }
  },
  watch: {
    show(val) {
      if (val) this.loadFacility();
    },
  },
  methods: {
    async loadFacility() {
      if (this.facility || !this.labTest?.facility_id) return;
      this.loading = true;
      try {
        const res = await apiClient.fetchFacility(this.labTest.facility_id);
        this.facility = res.data;
      } catch (e) {
        console.error("Could not load facility for lab report:", e);
      } finally {
        this.loading = false;
      }
    },
    printReport() {
      const style = document.createElement("style");
      style.id = "lab-report-print-style";
      style.textContent = `
        @page { size: A4 portrait; margin: 15mm 12mm 25mm; }
        @media print {
          body * { visibility: hidden; }
          #lab-report-content, #lab-report-content * { visibility: visible; }
          #lab-report-content {
            position: absolute; left: 0; top: 0; width: 100%;
            font-family: Arial, sans-serif; font-size: 10pt; color: #1a1a1a;
          }
        }
      `;
      document.head.appendChild(style);
      window.print();
      setTimeout(() => document.getElementById("lab-report-print-style")?.remove(), 1000);
    },

    formatDate(str) {
      if (!str) return "—";
      return new Date(str).toLocaleDateString("en-KE", { day: "2-digit", month: "short", year: "numeric" });
    },
    formatDateTime(str) {
      if (!str) return "—";
      return new Date(str).toLocaleString("en-KE", {
        day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
      });
    },
  },
};
</script>

<style scoped>
.print-footer { display: none; }

@media print {
  .print\:hidden { display: none !important; }

  .print-footer {
    display: block;
    position: fixed;
    bottom: 8mm;
    left: 0;
    right: 0;
    text-align: center;
    font-size: 9pt;
    color: #666;
    background: white;
    border-top: 1px solid #e5e7eb;
    padding-top: 4pt;
  }
  .print-footer::after {
    content: " — Page " counter(page);
  }
}
</style>

